/*
 * bbs-theme mobile UI redesign (2026-09-08 v2)
 * 手机端专属：底部导航栏、标签快速切换栏、列表项浏览数。
 * 电脑端不受影响（全部配套 CSS 限定在 @media max-width: 767px）。
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Component from 'flarum/common/Component';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import LogInModal from 'flarum/forum/components/LogInModal';
import Search from 'flarum/forum/components/Search';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
import UserCard from 'flarum/forum/components/UserCard';
import UserControls from 'flarum/common/utils/UserControls';
import Button from 'flarum/common/components/Button';
import CoverCropModal from './coverCropModal';
import icon from 'flarum/common/helpers/icon';
import avatar from 'flarum/common/helpers/avatar';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import QuoteBanner from './quoteBanner';
import './botGuard';

/* ---------- helpers ---------- */

function currentTagSlug() {
  try {
    const p = m.route.param();
    if (p && p.tags) return p.tags;
  } catch (e) {}
  try {
    return app.search.params().tags || null;
  } catch (e) {
    return null;
  }
}

function findVnodeByClass(root, cls) {
  if (!root || !Array.isArray(root.children)) return null;
  for (let i = 0; i < root.children.length; i++) {
    const c = root.children[i];
    if (
      c &&
      c.tag === 'div' &&
      c.attrs &&
      typeof c.attrs.className === 'string' &&
      c.attrs.className.indexOf(cls) !== -1
    ) {
      return c;
    }
    const deep = findVnodeByClass(c, cls);
    if (deep) return deep;
  }
  return null;
}

function findTag(slug) {
  if (!slug) return null;
  return (
    app.store.all('tags').find(
      (t) => t.slug().localeCompare(slug, undefined, { sensitivity: 'base' }) === 0
    ) || null
  );
}

function startDiscussion() {
  const tag = findTag(currentTagSlug());
  app.composer.load(DiscussionComposer, {
    user: app.session.user,
    tags: tag ? [tag] : [],
  });
  app.composer.show();
}

/* 主题切换：兼容 fof/nightmode 的偏好结构（登录用户写 preferences，游客写 cookie） */
function toggleNightMode() {
  const user = app.session.user;

  const isDark = () => {
    if (user && user.preferences) {
      const pref = user.preferences().fofNightMode;
      if (pref === 2) return true;
      if (pref === 1) return false;
    }
    const cookie = document.cookie.match(/flarum_nightmode=(\d+)/);
    if (cookie) return Number(cookie[1]) === 2;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const toDark = !isDark();

  if (user) {
    user.savePreferences({ fofNightMode: toDark ? 2 : 1 }).then(() => window.location.reload());
  } else {
    document.cookie =
      'flarum_nightmode=' +
      (toDark ? 2 : 1) +
      '; path=/; samesite=lax' +
      (location.protocol === 'https:' ? '; secure' : '');
    window.location.reload();
  }
}

/* ---------- 顶部标签快速切换栏 ---------- */

class MobileTagTabs extends Component {
  view() {
    const tags = app.store.all('tags').filter((t) => !t.parent() && t.position() !== null);
    const current = currentTagSlug();

    return m(
      'div.MobileTagTabs.mobile-only',
      { role: 'tablist' },
      m(
        'div.MobileTagTabs-scroller',
        m(
          'a.MobileTabItem',
          {
            className: !current ? 'active' : '',
            onclick: () => this.go(null),
          },
          '全部帖子'
        ),
        tags.map((t) =>
          m(
            'a.MobileTabItem',
            {
              className:
                current && current.localeCompare(t.slug(), undefined, { sensitivity: 'base' }) === 0
                  ? 'active'
                  : '',
              onclick: () => this.go(t.slug()),
            },
            t.name()
          )
        )
      )
    );
  }

  go(slug) {
    const params = app.search.params();
    if (slug) {
      params.tags = slug;
    } else {
      delete params.tags;
    }
    m.route.set(app.route('index', params));
  }
}

/* ---------- 底部导航栏 ---------- */

/* 2026-09-11: 跳转兜底——先在应用内路由，若 500ms 内地址与页面都没变化，再整页跳转。
   WebView（老内核）里偶尔出现「点了没反应」：可能 m.route.set 改了地址但页面没重绘，
   也可能什么都没发生；整页跳转一定能到，只是慢一点。 */
function bbsGo(url) {
  let u = url || '/';
  if (u.charAt(0) !== '/') u = '/' + u;
  const loc0 = window.location.pathname + window.location.search;
  if (loc0 === u) return;
  const sig = () => {
    try {
      const c = document.querySelector('.App-content');
      const f = c && c.firstElementChild;
      return ((app.current && app.current.get('routeName')) || '') + '|' + (f ? f.className : '') + '|' + (f ? f.childElementCount : -1);
    } catch (e) {
      return '';
    }
  };
  const s0 = sig();
  try {
    m.route.set(u);
  } catch (e) {}
  window.setTimeout(() => {
    let now = '';
    let s = '';
    try {
      now = window.location.pathname + window.location.search;
      s = sig();
    } catch (e) {}
    if (s === s0 && (now !== u || now === loc0)) window.location.href = u;
  }, 500);
}

class MobileBottomNav extends Component {
  view() {
    const user = (app.session && app.session.user) || null;
    let path = '';
    try {
      path = window.location.pathname || '';
    } catch (e) {}

    let unread = 0;
    try {
      unread = user && user.unreadNotificationsCount ? user.unreadNotificationsCount() : 0;
    } catch (e) {}

    // 2026-09-11: 底栏改成 <a href> + 拦截点击。WebView（老内核）里出现过
    // 「点了我的之后其它按钮都没反应」——SPA 路由没生效时不能卡死：
    // 1) 先试 m.route.set（正常情况走前端路由，不刷新）；
    // 2) 450ms 内地址没变就退回整页跳转（一定能跳过去，只是慢一点）；
    // 3) JS 整个挂掉时，<a href> 的默认行为仍然能跳。
    const go = (e, url) => {
      try {
        if (e && e.preventDefault) e.preventDefault();
      } catch (err) {}
      bbsGo(url);
    };

    return m(
      'nav.MobileBottomNav.mobile-only',
      m('button.MobileNavItem.MobileNav-theme', { onclick: toggleNightMode, title: '切换论坛主题' },
        icon('fas fa-adjust'), m('span.MobileNav-label', '主题')),
      m('a.MobileNavItem', {
        href: '/store-page',
        className: path.indexOf('/store-page') === 0 ? 'active' : '',
        onclick: (e) => go(e, app.route('storePage')),
      }, icon('fas fa-store'), m('span.MobileNav-label', '商店')),
      m('a.MobileNavItem', {
        href: '/',
        className: path === '/' || path === '/index' || path.indexOf('/?') === 0 ? 'active' : '',
        onclick: (e) => go(e, app.route('index')),
      }, icon('fas fa-list'), m('span.MobileNav-label', '帖子')),
      m('a.MobileNavItem', {
        href: '/notifications',
        className: path.indexOf('/notifications') === 0 ? 'active' : '',
        onclick: (e) => go(e, app.route('notifications')),
      },
        icon('far fa-bell'),
        unread > 0 && m('span.MobileNav-badge', unread > 99 ? '99+' : String(unread)),
        m('span.MobileNav-label', '通知')),
      user
        ? m('a.MobileNavItem', {
            href: app.route.user(user),
            className: path.indexOf('/u/') === 0 ? 'active' : '',
            onclick: (e) => go(e, app.route.user(user)),
          }, avatar(user), m('span.MobileNav-label', '我的'))
        : m('button.MobileNavItem', {
            onclick: () => app.modal.show(LogInModal),
          }, icon('fas fa-user'), m('span.MobileNav-label', '登录'))
    );
  }
}

/* ---------- hooks ---------- */

app.initializers.add('bbs-theme-mobile', () => {
  /* ---------- 搜索修复 ---------- */
  // 1) 回车 = 按输入框原文搜索。core 默认回车跳到下拉第 0 项的链接，而本站第 0 项
  //    是 best-answer 的“搜索已解决主题帖”建议 → 几乎永远 0 结果，搜索形同虚设。
  //    方向键明确选中其它建议（index≠0）时仍跟随该建议；点选不受影响。
  // 2) 下拉实时预览最小长度 3 → 2（中文常用词是 2 字，如“开销”）。
  Search.MIN_SEARCH_LEN = 2;
  Search.prototype.selectResult = function () {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.loadingSources = 0;

    const q = (this.searchState.getValue() || '').trim();
    let selectedUrl = null;
    try {
      selectedUrl = this.getItem(this.index).find('a').attr('href');
    } catch (e) {}

    if (this.index > 0 && selectedUrl) {
      m.route.set(selectedUrl);
    } else if (q) {
      m.route.set(app.route('index', { q }));
    } else {
      this.clear();
    }

    this.$('input').blur();
  };

  /* ---------- 顶部搜索：结果分「帖子」「用户」两类，点击标签切换（2026-09-13） ----------
     做法：覆盖核心 Search.prototype.view，按 sourceItems() 给出的 itemName 把结果源
     分成「帖子」（除 users 外的全部源，保留扩展自带的其它源）与「用户」（users 源）。
     标签栏用 li.Dropdown-header 承载（Dropdown-header 不进入可选中项，键盘导航不受影响），
     按钮 onmousedown preventDefault 防止输入框失焦、onclick stopPropagation 防止
     核心绑定在 .Search-results 上的 click→blur 把下拉关掉。不改 vendor，扩展停用即恢复原样。 */
  Search.prototype.bbsSearchTab = 'posts';

  Search.prototype.view = function () {
    const state = this.searchState;
    const currentSearch = state.getInitialSearch();

    if (!this.sources) {
      const items = this.sourceItems();
      let userSrc = null;
      try {
        if (items && typeof items.has === 'function' && items.has('users')) userSrc = items.get('users');
      } catch (e) {}
      this.sources = items.toArray();
      this.bbsUserSource = userSrc || null;
    }
    if (!this.sources.length) return m('div');

    const userSource = this.bbsUserSource;
    const postSources = [];
    const userSources = [];
    for (let i = 0; i < this.sources.length; i++) {
      const s = this.sources[i];
      const isUser = (s && s.itemName === 'users') || (!!userSource && s === userSource);
      if (isUser) userSources.push(s);
      else postSources.push(s);
    }
    const hasUsers = userSources.length > 0;
    const tab = this.bbsSearchTab === 'users' && hasUsers ? 'users' : 'posts';
    const activeSources = tab === 'users' ? userSources : postSources;

    const value = state.getValue() || '';
    const qLower = String(value).toLowerCase();
    const isActive = !!currentSearch;
    const shouldShowResults = !!(value && this.hasFocus);
    const shouldShowClearButton = !!(!this.loadingSources && value);
    const searchLabel = extractText(app.translator.trans('core.forum.header.search_placeholder'));

    const countOf = (list) => {
      let n = 0;
      for (let i = 0; i < list.length; i++) {
        const res = list[i].results;
        if (res && res.get) {
          const arr = res.get(qLower);
          if (arr && arr.length) n += arr.length;
        }
      }
      return n;
    };
    const cPosts = countOf(postSources);
    const cUsers = countOf(userSources);

    const body = [];
    if (shouldShowResults) {
      if (hasUsers) {
        const mkTab = (key, label, count) =>
          m(
            'button.bbs-search-tab',
            {
              type: 'button',
              className: tab === key ? 'active' : '',
              onmousedown: (e) => e.preventDefault(),
              onclick: (e) => {
                try {
                  e.stopPropagation();
                } catch (err) {}
                this.bbsSearchTab = key;
                this.hasFocus = true;
                m.redraw();
              },
            },
            count > 0 ? label + ' ' + count : label
          );
        body.push(m('li.Dropdown-header.bbs-search-tabs', [mkTab('posts', '帖子', cPosts), mkTab('users', '用户', cUsers)]));
      }

      for (let i = 0; i < activeSources.length; i++) {
        body.push(activeSources[i].view(value));
      }

      if (!this.loadingSources) {
        if (tab === 'users' && !cUsers) body.push(m('li.bbs-search-empty', '没有找到相关用户'));
        if (tab === 'posts' && !cPosts) body.push(m('li.bbs-search-empty', '没有找到相关帖子'));
      }
    }

    let cls = 'Search';
    if (value && this.hasFocus) cls += ' open';
    if (this.hasFocus) cls += ' focused';
    if (isActive) cls += ' active';
    if (this.loadingSources) cls += ' loading';

    return m(
      'div',
      { role: 'search', 'aria-label': app.translator.trans('core.forum.header.search_role_label'), className: cls },
      [
        m('div.Search-input', [
          m('input', {
            className: 'FormControl',
            type: 'search',
            placeholder: searchLabel,
            'aria-label': searchLabel,
            value: value,
            oninput: (e) => state.setValue(e.target.value),
            onfocus: () => (this.hasFocus = true),
            onblur: () => (this.hasFocus = false),
          }),
          this.loadingSources
            ? m(LoadingIndicator, { size: 'small', display: 'inline', containerClassName: 'Button Button--icon Button--link' })
            : null,
          shouldShowClearButton
            ? m(
                'button',
                {
                  className: 'Search-clear Button Button--icon Button--link',
                  type: 'button',
                  'aria-label': app.translator.trans('core.forum.header.search_clear_button_accessible_label'),
                  onclick: () => this.clear(),
                },
                icon('fas fa-times-circle')
              )
            : null,
        ]),
        m(
          'ul',
          {
            className: 'Dropdown-menu Search-results',
            'aria-hidden': shouldShowResults ? undefined : true,
            'aria-live': shouldShowResults ? 'polite' : undefined,
          },
          body
        ),
      ]
    );
  };

  /* ---------- 主页背景双端裁剪 ---------- */
  // 手机端用专属裁剪图（bbsCoverMobileUrl 由后端序列化属性提供）
  extend(UserCard.prototype, 'view', function (view) {
    const user = this.attrs.user;
    let mobileUrl = null;
    try {
      mobileUrl = user && user.attribute && user.attribute('bbsCoverMobileUrl');
    } catch (e) {}
    if (!mobileUrl) return;
    view.attrs.style = Object.assign({}, view.attrs.style, {
      '--bbs-cover-mobile': `url(${mobileUrl})`,
    });
  });

  // 用户菜单加“上传主页背景（双端裁剪）”入口
  extend(UserControls, 'moderationControls', function (items, user) {
    let can = false;
    try {
      can = user.canSetProfileCover && user.canSetProfileCover();
    } catch (e) {}
    if (!can) return;
    items.add(
      'bbsCoverCrop',
      m(
        Button,
        { icon: 'fas fa-crop-alt', onclick: () => app.modal.show(CoverCropModal, { user }) },
        '上传主页背景（双端裁剪）'
      ),
      -5
    );
  });

  // 底栏挂到 body（独立 root，全页面可见）；等 app 完全初始化后再 mount，
  // 避免 app.session 未就绪时首次渲染报错
  const el = document.createElement('div');
  el.className = 'mobile-bottom-nav-root';
  document.body.appendChild(el);
  const mountNav = () => m.mount(el, MobileBottomNav);
  if (app.initialized && typeof app.initialized.then === 'function') {
    app.initialized.then(mountNav, mountNav);
  } else {
    mountNav();
  }

  // 列表页插入标签切换栏（hero 之后、内容之前）
  extend(IndexPage.prototype, 'view', function (vnode) {
    if (!vnode.children || !vnode.children.length) return;
    const containerIdx = vnode.children.findIndex(
      (c) =>
        c &&
        c.tag === 'div' &&
        c.attrs &&
        typeof c.attrs.className === 'string' &&
        c.attrs.className.indexOf('container') >= 0
    );
    if (containerIdx < 0) return;
    const resultsVnode = findVnodeByClass(vnode, 'IndexPage-results');
    if (!resultsVnode || !resultsVnode.children) return;
    resultsVnode.children.unshift(m(MobileTagTabs));
  });

  // 列表项 info 行加浏览数（viewCount 由 michaelbelgium-discussion-views 提供）
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const discussion = this.attrs.discussion;
    let views = 0;
    try {
      views = discussion.viewCount ? discussion.viewCount() || 0 : 0;
    } catch (e) {}
    items.add(
      'bbs-views',
      m('span.DiscussionListItem-views', { title: '浏览数' }, icon('far fa-eye'), ' ', String(views)),
      50
    );
  });

  // 用户页手机端顶部下拉：折叠按钮首次进入显示“更多关于我的”（引导新用户），
  // 之后显示“更多关于我的：当前项”；展开后的菜单项一律显示“更多关于我的：原名”，
  // 方便区分主题/徽章等入口。仅手机端生效，SPA 路由切换后仍生效。
  const USER_NAV_PREFIX = '更多关于我的';
  const USER_NAV_SEEN_KEY = 'bbsUserNavSeen';
  let navLabelTimer = null;
  const stripUserNavPrefix = (s) => {
    const t = (s || '').trim();
    return t.indexOf(USER_NAV_PREFIX + '：') === 0 ? t.slice(USER_NAV_PREFIX.length + 1).trim() : t;
  };
  const fixUserNavLabel = () => {
    if (window.innerWidth > 767) return; // 只改手机端，电脑端不动
    const dd = document.querySelector('.UserPage-nav .Dropdown--select');
    if (!dd) return;
    // 菜单项：前缀 + 原名（只改第一个非空文本节点，不碰计数等其它子元素，
    // 否则 textContent 会把计数吸进文案并在 observer 循环里无限增长）
    dd.querySelectorAll('.Dropdown-menu .Button-label').forEach((el) => {
      for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType !== 3 || !node.textContent.trim()) continue;
        const orig = stripUserNavPrefix(node.textContent);
        if (!orig) break;
        const target = USER_NAV_PREFIX + '：' + orig;
        if (node.textContent !== target) node.textContent = target;
        break;
      }
    });
    // 折叠按钮：首次进入只显示“更多关于我的”（引导新用户知道在哪调整），
    // 之后显示“更多关于我的：当前项”。当前值恰好等于引导文案时保持不动。
    const toggleLabel = dd.querySelector('.Dropdown-toggle .Button-label');
    if (toggleLabel) {
      const first = !localStorage.getItem(USER_NAV_SEEN_KEY);
      const cur = toggleLabel.textContent.trim();
      let target;
      if (first || cur === USER_NAV_PREFIX) {
        target = USER_NAV_PREFIX;
      } else if (cur) {
        target = USER_NAV_PREFIX + '：' + stripUserNavPrefix(cur);
      } else {
        target = USER_NAV_PREFIX;
      }
      if (toggleLabel.textContent !== target) toggleLabel.textContent = target;
      if (first) localStorage.setItem(USER_NAV_SEEN_KEY, '1');
    }
  };
  const observer = new MutationObserver(() => {
    if (!document.querySelector('.UserPage-nav .Dropdown--select')) return;
    if (navLabelTimer) return;
    navLabelTimer = setTimeout(() => {
      navLabelTimer = null;
      fixUserNavLabel();
    }, 50);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  fixUserNavLabel();

  // 商店页顶部财富横幅：不给 xypp/store 源码动刀，直接 patch 其路由组件
  //（app.routes.storePage.component 由 xypp/store 初始化器注册，本扩展在其后启用；
  // 若顺序异常则等 app.initialized 后重试）。translator 在本站返回数组，
  // 因此横幅文案用纯字符串。
  const patchStorePage = () => {
    const route = app.routes['storePage'];
    const comp = route && route.component;
    if (!comp || !comp.prototype || comp.prototype.__bbsMoneyBanner) return false;
    comp.prototype.__bbsMoneyBanner = true;
    extend(comp.prototype, 'view', function (vnode) {
      if (!vnode || !vnode.children || vnode.children.length < 1) return;
      const user = (app.session && app.session.user) || null;
      let money = null;
      try {
        if (user) {
          // antoinefr/money 未定义 User.prototype.money，实际数据在原始 attributes 上
          if (typeof user.money === 'function') {
            money = Number(user.money() || 0);
          } else if (user.data && user.data.attributes && user.data.attributes.money != null) {
            money = Number(user.data.attributes.money || 0);
          }
        }
      } catch (e) {}
      const banner = m(
        'div.StoreMoneyBanner-wrap',
        m(
          'div.StoreMoneyBanner',
          m('span.StoreMoneyBanner-coin', { className: 'icon fas fa-coins' }),
          money === null
            ? m('span.StoreMoneyBanner-amount', '登录后可查看自己的财富')
            : m('span.StoreMoneyBanner-amount', '我的财富：' + String(money)),
          m(
            'div.StoreMoneyBanner-disclaimer',
            '本货币没有任何真实货币价值，不支持充值购买，不能兑换现金，不能与任何第三方平台进行交易。只能通过发帖和签到自动获得'
          )
        )
      );
      vnode.children.splice(1, 0, banner);
    });
    return true;
  };
  if (!patchStorePage()) {
    if (app.initialized && typeof app.initialized.then === 'function') {
      app.initialized.then(patchStorePage, patchStorePage);
    }
  }
});


/* ---------- OAuth 通用补丁（2026-09-09） ---------- */
// 适用于任何走 token 注册流的第三方登录（当前：hehongyuanlove/auth-qq）。
// 1) 手机端回调兜底：服务端 ResponseFactory 无 opener 时把 payload 存
//    localStorage('oauth_payload') 并跳首页，这里在页面加载时读出并交给 app。
// 2) token 注册弹框补密码框：core SignUpModal 在 token 模式不渲染密码字段，
//    会注册出服务端随机密码的账号，占位邮箱收不到重置邮件 → 永远无法密码登录。
app.initializers.add('bbs-oauth-payload', () => {
  try {
    const raw = localStorage.getItem('oauth_payload');
    if (raw) {
      localStorage.removeItem('oauth_payload');
      app.authenticationComplete(JSON.parse(raw));
    }
  } catch (e) {
    console.error('oauth payload pickup error', e);
  }
});

app.initializers.add('bbs-oauth-signup-password', () => {
  extend(SignUpModal.prototype, 'fields', function (items) {
    if (this.attrs.token && !items.has('password')) {
      items.add(
        'password',
        m(
          'div.Form-group',
          m('input.FormControl', {
            name: 'password',
            type: 'password',
            autocomplete: 'new-password',
            required: true,
            placeholder: app.translator.trans('core.forum.sign_up.password_placeholder'),
            'aria-label': app.translator.trans('core.forum.sign_up.password_placeholder'),
            bidi: this.password,
            disabled: this.loading,
          })
        ),
        10
      );
    }
  });
  extend(SignUpModal.prototype, 'submitData', function (data) {
    if (this.attrs.token) {
      data.password = this.password();
    }
  });
});


/* ---------- 首页诗词 portal（DOM 层插入，不进 vnode 树） ----------
 * @afrux/forum-widgets-core 硬编码 IndexPage children[1] = 内容容器；
 * 在 vnode 树里 splice 会把 container 挤走，统计卡片（在线/发帖/用户数）会消失。
 * 改为把横幅挂到 .App-content 第一个 DOM 子节点，完全绕开 vnode 结构。 */
function ensureQuotePortal() {
  let el = document.getElementById('bbs-quote-portal');
  if (!el) {
    const content = document.querySelector('.App-content');
    if (!content) return null;
    el = document.createElement('div');
    el.id = 'bbs-quote-portal';
    content.insertBefore(el, content.firstChild);
  }
  return el;
}
function showQuotePortal(slug) {
  try {
    const el = ensureQuotePortal();
    if (!el) return;
    el.style.display = '';
    if (el.dataset.slug !== String(slug)) {
      el.dataset.slug = String(slug);
      m.render(el, m(QuoteBanner, { slug }));
    }
  } catch (e) {}
}
function hideQuotePortal() {
  try {
    const el = document.getElementById('bbs-quote-portal');
    if (el) el.style.display = 'none';
  } catch (e) {}
}

/* ---------- QuoteBannerForIndexPage：主页横幅（2026-09-09 v4） ---------- */
// 关键：@afrux/forum-widgets-core 硬编码 IndexPage children[1] 必须是内容容器，
// 任何兄弟插入都会让它崩溃并白屏整页。因此：
//   首页诗词 -> 保持 children[0]（hero 之前，不挪动 container 位置）；
//   标签页语录 -> 插入 IndexPage-results 内部（移动端标签栏之后、帖子列表之上）。
extend(IndexPage.prototype, 'view', function (vnode) {
  try {
    if (!vnode.children || !vnode.children.length) return;
    const slug = currentTagSlug();
    if (!slug) {
      showQuotePortal(null);
      return;
    }
    hideQuotePortal();
    const allTags = app.store.all('tags');
    const is2D = String(slug)
      .split(',')
      .some((s) => {
        const t = allTags.find((x) => x.slug() === s.trim());
        return !!(t && /二次元|gal/i.test((t.name() || '') + ' ' + s.trim()));
      });
    window.__qbDebug = { slug: String(slug), is2D: is2D, found: false, t: Date.now() };
    if (!is2D) return;
    const results = findVnodeByClass(vnode, 'IndexPage-results');
    if (!results || !results.children) return;
    if (window.__qbDebug) window.__qbDebug.found = true;
    const off =
      results.children.length &&
      results.children[0] &&
      results.children[0].tag === MobileTagTabs
        ? 1
        : 0;
    results.children.splice(off, 0, m(QuoteBanner, { slug }));
  } catch (e) {
    // 横幅异常不影响页面
  }
});

/* ---------- 离开首页时移除诗词 portal ---------- */
extend(IndexPage.prototype, 'onremove', function () {
  try {
    const el = document.getElementById('bbs-quote-portal');
    if (el) el.remove();
  } catch (e) {}
});

/* ---------- 二次元/Galgame 区专属背景（2026-09-09） ---------- */
extend(IndexPage.prototype, 'view', function () {
  try {
    const slug = currentTagSlug();
    const isGal = String(slug || '')
      .split(',')
      .some((s) => /^galgame$/i.test(s.trim()));
    if (document.body) document.body.classList.toggle('bbs-gal-bg', isGal);
  } catch (e) {}
});


/* 手机端 Composer 自绘顶栏/毛玻璃/全屏层已移除（2026-09-11 用户要求回到 Flarum 原生编辑器）：
   原来是 .bbs-composer-nav + .bbs-composer-blur + bbs-composer-fullscreen，会盖住核心的
   发布/保存草稿按钮，禁用编辑区全屏后还会残留诡异的粉色遮罩，故整层删除。 */

/* ---------- 页面切换过渡动画（2026-09-10） ---------- */
import Page from 'flarum/common/components/Page';

let bbsLastRoute = null;
function bbsPageTransition() {
  try {
    const r = m.route.get();
    if (r === bbsLastRoute) return;
    bbsLastRoute = r;
    const b = document.body;
    b.classList.remove('bbs-page-enter');
    void b.offsetWidth; /* reflow 重启动画 */
    b.classList.add('bbs-page-enter');
  } catch (e) {}
}
extend(Page.prototype, 'oninit', bbsPageTransition);
extend(Page.prototype, 'onupdate', bbsPageTransition);

/* ---------- 进入帖子默认定位第一条 v2（2026-09-10） ---------- */
// v1 用 extend 挂在 oninit 之后，但原方法里 load() 同步发请求（near=0 已带出）→ 无效。
// v2 前置包装：原 oninit 执行前就设 near=1，load() 的请求即带 page near=1 → API 返回第 1 楼起。
// 带 near 参数（通知跳转/楼层直达）不受影响。
import DiscussionPage from 'flarum/forum/components/DiscussionPage';

const bbsOrigDPOninit = DiscussionPage.prototype.oninit;
DiscussionPage.prototype.oninit = function (...args) {
  try {
    if (!m.route.param('near')) this.near = 1;
    /* 二次元区帖子也挂背景类 */
    const d = this.discussion;
    if (d && d.tags) {
      const isGal = d.tags().some(t => /^galgame$/i.test(t.slug()));
      document.body.classList.toggle('bbs-gal-bg', isGal);
    } else {
      /* discussion 数据可能还没加载，延迟检测 */
      setTimeout(() => {
        try {
          const dd = this.discussion;
          if (dd && dd.tags) {
            const isGal = dd.tags().some(t => /^galgame$/i.test(t.slug()));
            document.body.classList.toggle('bbs-gal-bg', isGal);
          }
        } catch(e) {}
      }, 800);
    }
  } catch (e) {}
  return bbsOrigDPOninit.apply(this, args);
};

/* ---------- 进帖默认定位第一条 v3 兜底（2026-09-10） ---------- */
// show() 创建 PostStreamState 后的首次 goToNumber 是"进帖定位"的唯一出口，
// 在这里拦截不依赖请求时序/数据来源（find 或 preloaded 都覆盖）。
// URL 无 near 且目标不是 1 楼 → 强制定位 1 楼；后续跳转（楼层直达/scrubber 按钮）不受影响。
import PostStreamState from 'flarum/forum/states/PostStreamState';
import Composer from 'flarum/forum/components/Composer';

const bbsOrigPSGoTo = PostStreamState.prototype.goToNumber;
PostStreamState.prototype.goToNumber = function (number, noAnimation) {
  try {
    if (!this.__bbsFirstDone) {
      this.__bbsFirstDone = true;
      if (!m.route.param('near') && number !== 'reply' && number !== 1) {
        number = 1;
      }
    }
  } catch (e) {}
  return bbsOrigPSGoTo.call(this, number, noAnimation);
};

/* ---------- 列表标题点击永远进 1 楼（2026-09-10） ---------- */
// Flarum 默认列表标题链接带 jumpTo（最后阅读楼+1）→ URL 带 near → 进帖跳"最新回复"。
// 这里 override：普通列表点击返回 0（无 near，从 1 楼开始）；搜索结果保留直达相关帖。
const bbsOrigJumpTo = DiscussionListItem.prototype.getJumpTo;
DiscussionListItem.prototype.getJumpTo = function () {
  try {
    if (!this.attrs.params || !this.attrs.params.q) return 0;
  } catch (e) {}
  return bbsOrigJumpTo.call(this);
};

/* 手机端顶栏签到/发帖/全部主题：独立 root 自绘 v2（点击代理原按钮；标签菜单自绘） */
app.initializers.add('bbs-mobile-topbar', () => {
  const mount = () => {
    if (document.getElementById('bbs-mtb-root')) return;
    const root = document.createElement('div');
    root.id = 'bbs-mtb-root';
    root.innerHTML =
      '<button type="button" class="bbs-mtb-btn bbs-mtb-checkin" aria-label="签到"><i class="icon fas fa-calendar-check Button-icon"></i></button>' +
      '<button type="button" class="bbs-mtb-btn bbs-mtb-new" aria-label="发帖"><i class="icon fas fa-edit Button-icon"></i></button>';
    document.body.appendChild(root);
    root.querySelector('.bbs-mtb-new').addEventListener('click', () => {
      const t = document.querySelector('.item-newDiscussion button, .item-newDiscussion-1 button');
      if (t) { t.click(); return; }
      /* 帖子详情页没有 .item-newDiscussion：改点原生的「说点什么吧」回复框，打开回复编辑器（2026-09-11） */
      const ph = document.querySelector('.ReplyPlaceholder button, .ReplyPlaceholder');
      if (ph) ph.click();
    });
    root.querySelector('.bbs-mtb-checkin').addEventListener('click', () => {
      const t = document.querySelector('.item-forum-checkin button, .item-forum-checkin-1 button');
      if (t) t.click();
    });
    const sync = () => {
      const orig = document.querySelector('.item-forum-checkin button, .item-forum-checkin-1 button');
      const btn = root.querySelector('.bbs-mtb-checkin');
      if (orig && btn) btn.classList.toggle('bbs-mtb-done', orig.classList.contains('CheckInButton--green') || !!orig.disabled);
    };
    setInterval(sync, 2000);
    sync();

    /* 中间：全部主题下拉（自绘按钮+菜单，无抽屉依赖） */
    const mid = document.createElement('button');
    mid.type = 'button';
    mid.id = 'bbs-mtb-mid';
    document.body.appendChild(mid);
    const menu = document.createElement('div');
    menu.id = 'bbs-mtb-menu';
    document.body.appendChild(menu);
    /* 中间按钮数据源 = 核心 App-titleControl 的下拉菜单（2026-09-11 统一，动态抓取）
       - 用户页/设置页 .UserPage-nav → 按钮变「更多」+ 用户导航（回复/主题/赞/…/设置/安全）
       - 首页 .IndexPage-nav → 全部主题 + 私密主题/我的关注/Badges/商店/任务/标签 + 各标签 */
    const collectNav = (scope) => {
      try {
        const nav = document.querySelector(scope);
        if (!nav) return null;
        // 注意：不要用 [...nodeList]（babel loose 会编译成 [].concat(nodeList)，
        // concat 不会展开 NodeList → 整个函数静默报错返回 null）。plain for 循环最稳。
        const lis = nav.querySelectorAll('.App-titleControl .Dropdown-menu > li');
        const items = [];
        for (let i = 0; i < lis.length; i++) {
          const li = lis[i];
          if (li.classList.contains('Dropdown-separator')) { items.push({ sep: true }); continue; }
          const a = li.querySelector('a');
          if (!a) continue;
          const labelEl = a.querySelector('.Button-label');
          const badgeEl = a.querySelector('.Button-badge');
          const iconEl = a.querySelector('i.Button-icon') || a.querySelector('i.icon');
          // 只取第一个文本节点：否则 textContent 会把嵌套的 .Button-badge 数字吸进文案
          let text = '';
          const holder = labelEl || a;
          for (let j = 0; j < holder.childNodes.length; j++) {
            const node = holder.childNodes[j];
            if (node.nodeType === 3 && node.textContent.trim()) { text = node.textContent; break; }
          }
          if (!text) text = holder.textContent || '';
          text = text.replace(/^\s*更多关于我的[：:]\s*/, '').replace(/\s+/g, ' ').trim();
          const href = a.getAttribute('href');
          if (!text || !href) continue;
          const iconCls = iconEl ? String(iconEl.className || '').split(/\s+/).filter((c) => c && c !== 'icon' && c !== 'Button-icon').join(' ') : '';
          items.push({
            text: text,
            href: href,
            count: badgeEl ? badgeEl.textContent.trim() : '',
            icon: iconCls,
            active: li.classList.contains('active'),
          });
        }
        while (items.length && items[0].sep) items.shift();
        while (items.length && items[items.length - 1].sep) items.pop();
        return items.length ? items : null;
      } catch (e) { return null; }
    };
    const isNotifPage = () => {
      try {
        if (app.current && app.current.get && app.current.get('routeName') === 'notifications') return true;
      } catch (e) {}
      return /^\/notifications(\/|$)/.test(location.pathname);
    };
    /* 草稿页（/drafts，fof/drafts）：核心自带整页界面，自绘顶栏会压住它的按钮（2026-09-13） */
    const isDraftsPage = () => {
      try {
        if (app.current && app.current.get && app.current.get('routeName') === 'drafts') return true;
      } catch (e) {}
      return /^\/drafts(\/|$)/.test(location.pathname);
    };
    /* 帖子详情页：CSS 靠 body.bbs-disc 去掉主题的「全部主题」，
       并把 签到/发帖 整组左移，给核心右上角的「更多」三点让位（2026-09-11） */
    const isDiscPage = () => {
      try {
        if (app.current && app.current.get && app.current.get('routeName') === 'discussion') return true;
      } catch (e) {}
      return /^\/d\//.test(location.pathname);
    };
    /* 标签页（/t/xxx）：核心的「订阅/收藏」星按钮会压住主题的签到键，
       CSS 靠 body.bbs-tag 把它挪到左侧（2026-09-11 用户反馈） */
    const isTagPage = () => {
      try {
        if (app.current && app.current.get && app.current.get('routeName') === 'tag') return true;
      } catch (e) {}
      return /^\/t\//.test(location.pathname);
    };
    /* 个人页（/u/xxx 以及它下面的子页）：右上角的自绘「签到/发帖」和页面内容重复，
       用户要求不展示（2026-09-12）。只藏 root 那一组，「更多」还要留着开用户菜单 */
    const isUserPage = () => {
      try {
        const rn = app.current && app.current.get && app.current.get('routeName');
        if (rn && rn.indexOf('user') === 0) return true;
      } catch (e) {}
      return /^\/u\//.test(location.pathname);
    };
    const renderItems = (items) => {
      let html = '';
      items.forEach((it) => {
        if (it.sep) { html += '<div class="bbs-mtb-menu-sep"></div>'; return; }
        html += '<div class="bbs-mtb-menu-item' + (it.active ? ' bbs-mtb-menu-item--active' : '') + '"'
          + (it.href ? ' data-href="' + it.href + '"' : '') + '>'
          + (it.icon ? '<i class="icon ' + it.icon + '"></i>' : '')
          + '<span class="bbs-mtb-menu-label">' + it.text + '</span>'
          + (it.count ? '<span class="bbs-mtb-menu-count">' + it.count + '</span>' : '')
          + '</div>';
      });
      return html;
    };
    const fill = () => {
      const userNav = collectNav('.UserPage-nav');
      if (userNav) { menu.innerHTML = renderItems(userNav); return; }
      /* 首页：直接用核心「全部主题」下拉的完整列表
         （全部主题 / 私密主题 / 我的关注 / Badges / 商店 / 任务 / 标签 + 各标签，2026-09-11） */
      const idxNav = collectNav('.IndexPage-nav');
      if (idxNav) { menu.innerHTML = renderItems(idxNav); return; }
      const tags = app.store.all('tags').filter((t) => !t.parent() && t.position() !== null);
      let html = '<div class="bbs-mtb-menu-item" data-slug="">全部主题</div>';
      tags.forEach((t) => { html += '<div class="bbs-mtb-menu-item" data-slug="' + t.slug() + '">' + t.name() + '</div>'; });
      menu.innerHTML = html;
    };
    let lastLabel = null;
    let lastHide = null;
    let lastDisc = null;
    let lastTag = null;
    let lastUser = null;
    let lastCore = null;
    /* 核心页面自带居中标题（h4.App-titleControl--text）时（通知中心/小黑屋/草稿/设置等），
       主题的「全部主题」压在上面会叠字：只留页面自己的标题。
       用 body 类 + CSS 判断，不用 :has()（老 WebView 不支持，2026-09-13）。 */
    const hasCoreTitle = () => {
      try {
        const hs = document.querySelectorAll('h4.App-titleControl--text');
        for (let i = 0; i < hs.length; i++) {
          const h = hs[i];
          if (h.offsetWidth > 0 && window.getComputedStyle(h).visibility !== 'hidden') return true;
        }
      } catch (e) {}
      return false;
    };
    const update = () => {
      /* 通知中心/草稿：自绘的 签到/发帖/全部主题 会压住页面自带的标题与图标
         （2026-09-11 通知页，2026-09-13 加上草稿页） */
      const hide = isNotifPage() || isDraftsPage();
      /* 个人页只藏「签到/发帖」那一组（2026-09-12） */
      const userPg = isUserPage();
      /* 小黑屋等核心页面自带居中标题时，「全部主题」也要收掉，避免叠字（2026-09-13） */
      const coreTitle = hasCoreTitle();
      if (hide !== lastHide || userPg !== lastUser || coreTitle !== lastCore) {
        lastHide = hide;
        lastUser = userPg;
        lastCore = coreTitle;
        root.classList.toggle('bbs-mtb-hide', hide || userPg);
        mid.classList.toggle('bbs-mtb-hide', hide || coreTitle);
        if (hide || coreTitle) menu.style.display = 'none';
      }
      const disc = isDiscPage();
      if (disc !== lastDisc) {
        lastDisc = disc;
        try { document.body.classList.toggle('bbs-disc', disc); } catch (e) {}
        if (disc) menu.style.display = 'none';
      }
      const tag = isTagPage();
      if (tag !== lastTag) {
        lastTag = tag;
        try { document.body.classList.toggle('bbs-tag', tag); } catch (e) {}
      }
      let txt = '全部主题';
      try {
        if (collectNav('.UserPage-nav')) {
          txt = '更多';
        } else {
          const idx = collectNav('.IndexPage-nav');
          if (idx) {
            for (let i = 0; i < idx.length; i++) {
              if (idx[i].active) { txt = idx[i].text; break; }
            }
          }
        }
      } catch (e) {}
      if (txt !== lastLabel) {
        lastLabel = txt;
        mid.innerHTML = '<span>' + txt + '</span><i class="icon fas fa-sort"></i>';
      }
    };
    update();
    fill();
    /* 路由切换/重绘后立刻刷新；低频定时器兜底。
       别在挂载时等 app.initialized / setTimeout(1500)——那会让按钮「加载很慢」。 */
    extend(Page.prototype, 'onupdate', () => { try { update(); } catch (e) {} });
    setInterval(update, 800);
    mid.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isNotifPage() || isDraftsPage()) return;
      fill();
      menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
    });
    menu.addEventListener('click', (e) => {
      const item = e.target.closest('.bbs-mtb-menu-item');
      if (!item) return;
      menu.style.display = 'none';
      // 2026-09-11: 同样用 bbsGo 兜底（路由没生效就整页跳）
      const goMenu = (u) => bbsGo(u);
      const href = item.getAttribute('data-href');
      if (href) {
        if (/^https?:/i.test(href)) window.location.href = href;
        else goMenu(href);
        return;
      }
      const slug = item.getAttribute('data-slug');
      goMenu(slug ? app.route('index', { tags: slug }) : app.route('index'));
    });
    document.addEventListener('click', (e) => {
      if (menu.style.display === 'block' && !menu.contains(e.target) && e.target !== mid && !mid.contains(e.target)) menu.style.display = 'none';
    });
  };
  /* 立刻挂载：等 app.initialized / setTimeout(1500) 会让 签到/发帖/全部主题 出现得很慢 */
  try { mount(); } catch (e) {}
  try {
    if (app.initialized && app.initialized.then) app.initialized.then(() => { try { mount(); } catch (e) {} });
  } catch (e) {}
});

/* 原生 Composer 手机端增强（2026-09-11）
   - 全屏按钮：直接用核心自带的 li.item-fullScreen，只在 CSS 里把它放出来（零 JS）
   - 毛玻璃遮罩：mithril 组件按 app.composer 状态显隐；开合时立刻重绘，
     不用轮询反复 m.mount（那会让入场动画每 500ms 重放、闪一下） */
app.initializers.add('bbs-composer-native-extras', () => {
  const isPhone = () => (window.innerWidth || 0) <= 767;
  const host = document.createElement('div');
  host.id = 'bbs-cm-blur-host';
  document.body.appendChild(host);
  let wantShow = false;
  /* force: true=确定显示(show)，false=确定隐藏(hide/minimize)；不传则按当前 DOM 推断
     （最小化后 composer 会挂 .minimized 类，此时不该再铺遮罩） */
  /* 帖子详情页里的编辑器 = 回复，贴底半屏且不铺毛玻璃（别盖住上面的回复）；
     首页发主题仍是顶部半屏 + 下半部分毛玻璃（2026-09-11） */
  const isReplyMode = () => {
    try { return /^\/d\//.test(location.pathname); } catch (e) { return false; }
  };
  let wantReply = false;
  const sync = (force) => {
    let w = false;
    try {
      if (typeof force === 'boolean') w = force;
      else w = !!app.composer && app.composer.isVisible() && !document.querySelector('.Composer.minimized');
    } catch (e) { w = false; }
    w = !!w && isPhone();
    const rp = w && isReplyMode();
    if (rp !== wantReply) {
      wantReply = rp;
      try { document.body.classList.toggle('bbs-reply-sheet', rp); } catch (e) {}
    }
    if (w !== wantShow) { wantShow = w; m.redraw(); }
  };
  m.mount(host, {
    view: () => (wantShow && !wantReply
      ? m('div.bbs-native-blur', {
        onclick: () => { try { app.composer.minimize(); } catch (e) {} },
      })
      : null),
  });
  [['show', true], ['hide', false], ['minimize', false]].forEach((pair) => {
    try {
      const k = pair[0];
      const orig = app.composer && app.composer[k];
      if (typeof orig !== 'function' || orig.__bbsWrapped) return;
      const wrapped = function () {
        const r = orig.apply(this, arguments);
        sync(pair[1]);
        return r;
      };
      wrapped.__bbsWrapped = true;
      app.composer[k] = wrapped;
    } catch (e) {}
  });
  sync();
  setInterval(() => sync(), 500);
});

/* bbs-composer-galbg-fix removed (MutationObserver caused browser freeze) */

/* Composer 粉色修复：extend oncreate 直接写内联 style，零依赖 */
app.initializers.add('bbs-composer-extend-fix', () => {
  extend(Composer.prototype, 'oncreate', function () {
    try {
      const isGal = document.body.classList.contains('bbs-gal-bg')
        || (m.route.param('tags') || '').split(',').some(t => /^galgame$/i.test(t.trim()));
      if (!isGal) return;
      // 强制白底
      if (this.element) {
        this.element.style.setProperty('background', '#fff', 'important');
      }
      const body = this.element && this.element.querySelector('.ComposerBody');
      if (body) body.style.setProperty('background', '#fff', 'important');
      // 确保body还有gal-bg类
      document.body.classList.add('bbs-gal-bg');
    } catch (e) {}
  });
  // Also patch onupdate for when Composer transitions (minimized -> fullscreen etc)
  extend(Composer.prototype, 'onupdate', function () {
    try {
      const isGal = document.body.classList.contains('bbs-gal-bg')
        || (m.route.param('tags') || '').split(',').some(t => /^galgame$/i.test(t.trim()));
      if (!isGal) return;
      if (this.element) {
        this.element.style.setProperty('background', '#fff', 'important');
      }
      const body = this.element && this.element.querySelector('.ComposerBody');
      if (body) body.style.setProperty('background', '#fff', 'important');
      document.body.classList.add('bbs-gal-bg');
    } catch (e) {}
  });
});
/* ============================================================
 * 手机端「添加到桌面」（v3 - 安卓套壳 APK 版）
 * - 安卓：点一下直接触发直链下载 bbs.apk（WebView 套壳客户端），
 *   并弹出安装说明；不是安卓则明确提示「仅支持安卓系统」
 * - iPhone / 电脑：给出手动添加说明（iOS 分享→添加到主屏幕）
 * - 若浏览器支持原生安装（beforeinstallprompt），额外给一个备用按钮
 * - ✕ 关闭当天不再显示；已安装（standalone）或桌面端不显示
 * ============================================================ */
app.initializers.add('bbs-pwa-install', () => {
  const DISMISS_KEY = 'bbs-pwa-dismissed';
  const APK_URL = '/download/bbs-2.1.apk?v=2.1';
  const today = () => new Date().toISOString().slice(0, 10);
  let deferredPrompt = null;
  let btnEl = null;
  let guideEl = null;

  try {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', function () {
        navigator.serviceWorker.register('/sw.js?v=2').catch(function () {});
      });
    }
  } catch (e) {}

  // [2026-09-12] 站点 <head> 里有一段极早的内联脚本，先把浏览器的
  // beforeinstallprompt preventDefault 掉并存进 window.__bbsBIP：Flarum 的
  // bundle 要 1~2 秒才执行，等它注册监听时 Chrome 可能已经把自带的
  // 「是否将网站添加到桌面」小黄条弹出来了。这里接手那个事件，只留我们的小按钮。
  function adoptBIP(e) {
    if (!e || typeof e.prompt !== 'function') return;
    try { e.preventDefault(); } catch (err) {}
    deferredPrompt = e;
    if (guideEl) {
      const retry = guideEl.querySelector('.bbs-pwa-guide-retry');
      if (retry) {
        retry.textContent = '现在可以直接安装了，点这里喵~';
        retry.classList.add('is-ready');
      }
    }
    ensureButton();
  }

  window.addEventListener('beforeinstallprompt', function (e) {
    adoptBIP(e);
  });

  window.addEventListener('bbs-bip', function (ev) {
    adoptBIP((ev && ev.detail) || window.__bbsBIP);
  });

  try { adoptBIP(window.__bbsBIP); } catch (err) {}

  window.addEventListener('appinstalled', function () {
    removeGuide();
    removeButton();
  });

  function isMobile() { return window.innerWidth <= 767; }

  function isAndroid() { return /Android/i.test(navigator.userAgent || ''); }

  function isStandalone() {
    try {
      return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
        || window.navigator.standalone === true;
    } catch (e) { return false; }
  }

  function dismissedToday() {
    try { return localStorage.getItem(DISMISS_KEY) === today(); } catch (e) { return false; }
  }

  function toolbar() {
    return document.querySelector('.IndexPage .IndexPage-toolbar') || document.querySelector('.IndexPage-toolbar');
  }

  function doPrompt() {
    if (!deferredPrompt) return false;
    const p = deferredPrompt;
    deferredPrompt = null;
    try {
      p.prompt();
      if (p.userChoice && p.userChoice.then) {
        p.userChoice.then(function () {}).catch(function () {});
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ---------- 直链下载 APK ---------- */
  function downloadApk() {
    try {
      const a = document.createElement('a');
      a.href = APK_URL;
      a.setAttribute('download', 'bbs-2.1.apk');
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { if (a.parentElement) a.parentElement.removeChild(a); }, 0);
      return true;
    } catch (e) {
      try { window.location.href = APK_URL; return true; } catch (e2) { return false; }
    }
  }

  /* ---------- 说明弹层 ---------- */
  function otherSteps() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isWX = /MicroMessenger/i.test(ua);
    const isQQ = /QQBrowser|MQQBrowser|\bQQ\//i.test(ua);
    if (isIOS) {
      return ['iPhone 请在 Safari 打开本站', '点底部「分享」按钮 → 选「添加到主屏幕」'];
    }
    if (isWX) {
      return ['点右上角「⋯」→ 选「在浏览器中打开」', '换安卓手机浏览器打开后可下载安卓客户端'];
    }
    if (isQQ) {
      return ['点右上角菜单 → 「在浏览器中打开」', '换安卓手机浏览器打开后可下载安卓客户端'];
    }
    return ['请用安卓手机打开本站，点一下就能下载客户端', 'iPhone 可用 Safari「分享 → 添加到主屏幕」'];
  }

  function openGuide(mode) {
    if (guideEl) removeGuide();
    const isApk = mode === 'apk';
    const title = isApk ? '开始下载安卓版喵~' : '添加到桌面';
    const sub = isApk
      ? '装好后桌面就有「交流区喵」图标，点一下直达论坛（仅支持安卓系统）'
      : '这个功能仅支持安卓系统喵~ iPhone 可以照下面加个桌面图标';
    const steps = isApk
      ? ['安装包会自动开始下载（很小，几秒就好）', '下载完点通知栏里的安装包完成安装', '若提示「未知来源」，允许一次就好啦', '打开如果闪退了，把屏幕上的红字截图发我喵~']
      : otherSteps();

    guideEl = document.createElement('div');
    guideEl.className = 'bbs-pwa-guide';
    guideEl.innerHTML = '<div class="bbs-pwa-guide-card">'
      + '<div class="bbs-pwa-guide-title">' + title + '</div>'
      + '<div class="bbs-pwa-guide-sub">' + sub + '</div>'
      + (isApk ? '<div class="bbs-pwa-guide-note">保证无毒，有毒站长原地爆炸喵～🐱</div>' : '')
      + '<ol class="bbs-pwa-guide-steps">' + steps.map(function (s) { return '<li>' + s + '</li>'; }).join('') + '</ol>'
      + (isApk ? '<a class="bbs-pwa-guide-dl" href="' + APK_URL + '" download="bbs-2.1.apk" rel="noopener">没开始？点这里直接下载</a>' : '')
      + (!isApk && deferredPrompt ? '<button type="button" class="bbs-pwa-guide-retry is-ready">现在可以直接安装了，点这里喵~</button>' : '')
      + (!isApk && !deferredPrompt ? '<button type="button" class="bbs-pwa-guide-retry">再试一次直接安装</button>' : '')
      + '<button type="button" class="bbs-pwa-guide-ok">知道啦</button>'
      + '</div>';
    document.body.appendChild(guideEl);
    guideEl.addEventListener('click', function (ev) { if (ev.target === guideEl) removeGuide(); });
    guideEl.querySelector('.bbs-pwa-guide-ok').addEventListener('click', removeGuide);
    const dl = guideEl.querySelector('.bbs-pwa-guide-dl');
    if (dl) {
      dl.addEventListener('click', function (ev) { ev.preventDefault(); downloadApk(); });
    }
    const retry = guideEl.querySelector('.bbs-pwa-guide-retry');
    if (retry) {
      retry.addEventListener('click', function () {
        if (doPrompt()) { removeGuide(); return; }
        this.textContent = '这个浏览器不支持直接装，照上面的步骤手动加一下吧喵~';
        this.classList.remove('is-ready');
      });
    }
  }

  function removeGuide() {
    if (guideEl && guideEl.parentElement) guideEl.parentElement.removeChild(guideEl);
    guideEl = null;
  }

  /* ---------- 小按钮 ---------- */
  function removeButton() {
    if (btnEl && btnEl.parentElement) btnEl.parentElement.removeChild(btnEl);
    btnEl = null;
  }

  function ensureButton() {
    try {
      if (!isMobile() || isStandalone() || dismissedToday()) { removeButton(); return; }
      const bar = toolbar();
      if (!bar) { removeButton(); return; }
      if (btnEl && btnEl.isConnected && btnEl.parentElement === bar) return;
      removeButton();

      btnEl = document.createElement('div');
      btnEl.className = 'bbs-pwa-install';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bbs-pwa-btn';
      btn.innerHTML = '<i class="icon fas fa-mobile-alt"></i><span>添加到桌面</span>';
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        if (isAndroid()) {
          downloadApk();
          openGuide('apk');
          return;
        }
        if (!doPrompt()) openGuide('other');
        else removeGuide();
      });

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'bbs-pwa-close';
      close.setAttribute('aria-label', '不再提示');
      close.innerHTML = '&times;';
      close.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        try { localStorage.setItem(DISMISS_KEY, today()); } catch (e) {}
        removeButton();
        removeGuide();
      });

      btnEl.appendChild(btn);
      btnEl.appendChild(close);

      const anchor = bar.querySelector('.Dropdown--select') || bar.firstElementChild;
      if (anchor && anchor.parentElement === bar) {
        anchor.insertAdjacentElement('afterend', btnEl);
      } else {
        bar.appendChild(btnEl);
      }
    } catch (e) {}
  }

  setTimeout(ensureButton, 1500);
  setInterval(ensureButton, 1500);
});

/* ---------- QQ 登录回调兜底（2026-09-10） ----------
 * 背景：QQ/TBS 内置浏览器里 window.opener.app 取不到会直接抛错，
 * 原版回调页因此中断在第一步，用户看到全白页。
 * 服务端已换成 RobustQQResponseFactory（带 try/catch + localStorage + hash 三层兜底），
 * 这里负责把 hash 里的 payload 取出来交给 app（localStorage 可能在 X5 里被隔离）。 */
app.initializers.add('bbs-oauth-hash', () => {
  try {
    const h = String(location.hash || '');
    const key = '#bbs-oauth=';
    if (h.indexOf(key) === 0) {
      const raw = decodeURIComponent(h.substring(key.length));
      try {
        history.replaceState(null, '', location.pathname + location.search);
      } catch (e) {
        location.hash = '';
      }
      app.authenticationComplete(JSON.parse(raw));
    }
  } catch (e) {
    console.error('oauth hash pickup error', e);
  }
});

/* ---------- [2026-09-13] 时间显示规则（发帖 / 回复 / 列表 / 详情页楼顶） ----------
 * 站长指定的规则：
 *   今天：刚刚 / N分钟前 / 超过一小时 → 今天11:20
 *   昨天 → 昨天11:20
 *   昨天以前（同一年）→ 9/10 11:20
 *   跨年 → 2024/5/2 11:20
 *   帖子详情页楼顶（#1）→ 发布时间：2024-05-20 20:30（绝对时间）
 * 说明：核心把相对时间渲染成 <time data-humantime datetime="...">4 天前</time>，
 *   且核心 liveHumanTimes 每 10 秒会用 dayjs 重写一次文本；所以这里格式化之后
 *   把 data-humantime 摘掉、改挂 data-bbs-time / data-bbs-abs，核心就不会再覆盖，
 *   再由自己的 2 秒轮询兜底（新渲染出来的节点 + 跨分钟/跨天自动更新）。
 */
function bbsTimePad2(n) { return n < 10 ? '0' + n : '' + n; }

function bbsTimeFmt(value) {
  var d = value instanceof Date ? value : new Date(value);
  if (!d || isNaN(d.getTime())) return null;
  var now = new Date();
  var diff = now.getTime() - d.getTime();
  if (diff < 0) diff = 0;
  var hm = d.getHours() + ':' + bbsTimePad2(d.getMinutes());
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) {
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前';
    return '今天' + hm;
  }
  var yst = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (d.getFullYear() === yst.getFullYear() && d.getMonth() === yst.getMonth() && d.getDate() === yst.getDate()) return '昨天' + hm;
  var md = d.getMonth() + 1 + '/' + d.getDate() + ' ' + hm;
  if (d.getFullYear() === now.getFullYear()) return md;
  return d.getFullYear() + '/' + md;
}

function bbsTimeAbs(value) {
  var d = value instanceof Date ? value : new Date(value);
  if (!d || isNaN(d.getTime())) return null;
  return d.getFullYear() + '-' + bbsTimePad2(d.getMonth() + 1) + '-' + bbsTimePad2(d.getDate()) + ' ' + bbsTimePad2(d.getHours()) + ':' + bbsTimePad2(d.getMinutes());
}

function bbsPatchTimeNodes() {
  var i, el, iso, label, items, nodes;
  // 1) 帖子详情页楼顶：PostStream-item[data-number="1"] → 发布时间：绝对时间
  try {
    items = document.querySelectorAll('.PostStream-item[data-number]');
    for (i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-number') !== '1') continue;
      el = items[i].querySelector('.PostMeta time');
      if (!el) continue;
      iso = el.getAttribute('datetime') || items[i].getAttribute('data-time');
      if (!iso) continue;
      label = bbsTimeAbs(iso);
      if (!label) continue;
      label = '发布时间：' + label;
      if (el.hasAttribute('data-humantime')) el.removeAttribute('data-humantime');
      if (el.getAttribute('data-bbs-abs') !== iso) el.setAttribute('data-bbs-abs', iso);
      if (el.textContent !== label) el.textContent = label;
    }
  } catch (e) {}
  // 2) 其余相对时间：列表 / 回复 / 通知等，统一按规则显示
  try {
    nodes = document.querySelectorAll('time[data-humantime], time[data-bbs-time]');
    for (i = 0; i < nodes.length; i++) {
      el = nodes[i];
      if (el.hasAttribute('data-bbs-abs')) continue;
      iso = el.getAttribute('datetime') || el.getAttribute('data-bbs-time');
      if (!iso) continue;
      label = bbsTimeFmt(iso);
      if (!label) continue;
      if (el.hasAttribute('data-humantime')) el.removeAttribute('data-humantime');
      if (!el.hasAttribute('data-bbs-time')) el.setAttribute('data-bbs-time', iso);
      if (el.textContent !== label) el.textContent = label;
    }
  } catch (e) {}
}

app.initializers.add('bbs-time-format', () => {
  const runTimeFormat = () => { try { bbsPatchTimeNodes(); } catch (e) {} };
  runTimeFormat();
  extend(Page.prototype, 'onupdate', runTimeFormat);
  setInterval(runTimeFormat, 2000);
});

// ===== [2026-09-14] HG3 品牌块：桌面右栏挂件列末尾 + 手机抽屉会话项下方 =====
var BBS_HG3_IMG = '/assets/bbs/hg3-logo.png?v=1';
var BBS_HG3_LINES = ['黄鹄一举兮知高', 'Hidden Gem', '交流区'];

function bbsHg3Make(tag) {
  var box = document.createElement(tag);
  box.className = 'bbs-hg3-card';
  var img = document.createElement('img');
  img.className = 'bbs-hg3-img';
  img.setAttribute('src', BBS_HG3_IMG);
  img.setAttribute('alt', '交流区');
  img.setAttribute('loading', 'lazy');
  box.appendChild(img);
  var wrap = document.createElement('div');
  wrap.className = 'bbs-hg3-txt';
  var cls = ['bbs-hg3-l1', 'bbs-hg3-l2', 'bbs-hg3-l3'];
  for (var i = 0; i < BBS_HG3_LINES.length; i++) {
    var d = document.createElement('div');
    d.className = cls[i];
    d.textContent = BBS_HG3_LINES[i];
    wrap.appendChild(d);
  }
  box.appendChild(wrap);
  return box;
}

// 桌面右栏：追加到挂件列末尾（论坛统计下方）；手机端由 CSS 隐藏这一份
function bbsHg3Desktop() {
  var sec = document.querySelector('.IndexPage .AfruxWidgets-WidgetSection.AfruxWidgets-sideNavAlt');
  if (!sec) return;
  if (sec.querySelector('.bbs-hg3-card')) return;
  sec.appendChild(bbsHg3Make('div'));
}

// 手机抽屉：插在会话项（crsyyds）后面；桌面端由 CSS 隐藏这一份
// 注意：抽屉里有两个 ul.Header-controls（Header-primary 在手机端是空的），
// 真正装菜单项的是包含 .item-session 的那个，必须按会话项反查父节点。
function bbsHg3Mobile() {
  var sess = document.querySelector('.App-drawer .item-session');
  var list = sess && sess.parentNode ? sess.parentNode
    : document.querySelector('.App-drawer .Header-secondary .Header-controls');
  if (!list) return;
  var keep = list.querySelector('.bbs-hg3-drawer');
  // 清理插错位置的残留卡片（同一页面会话内）
  var stray = document.querySelectorAll('.bbs-hg3-drawer');
  for (var i = 0; i < stray.length; i++) {
    if (stray[i] !== keep && stray[i].parentNode) stray[i].parentNode.removeChild(stray[i]);
  }
  if (keep) return;
  var li = bbsHg3Make('li');
  li.className = 'bbs-hg3-card bbs-hg3-drawer';
  if (sess && sess.parentNode === list) list.insertBefore(li, sess.nextSibling);
  else list.appendChild(li);
}

app.initializers.add('bbs-hg3-brand', function () {
  var bbsHg3Run = function () {
    try { bbsHg3Desktop(); } catch (e) {}
    try { bbsHg3Mobile(); } catch (e) {}
  };
  bbsHg3Run();
  extend(Page.prototype, 'onupdate', bbsHg3Run);
  setInterval(bbsHg3Run, 1500);
});
